import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthRouting, useSignedInUserState } from "../hooks/useAuthState";
import { restaurantService } from "../services/firebase/restaurantManagementService";
import { FullProperty } from "../models/fullProperty";
import { SaleItem } from "../models/saleItem";
import { toast } from "react-toastify";
import { Badge, Box, Button, CircularProgress, Drawer, IconButton, List, ListItem, ListItemButton, ListItemText, Modal, TextField, Typography } from "@mui/material";
import { StringsService } from "../services/strings/strings";
import { CartService } from "../services/functions/cartService";
import { AuthButton } from "../shared/authButton";
import MessagesPage from "../messagesPage/messagesPage";
import MailIcon from "@mui/icons-material/Mail";
import EngineeringIcon from '@mui/icons-material/Engineering';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useActiveMessages } from "../hooks/useUnreadMessages";
import { useRestaurantOrderHistory } from "../hooks/useRestaurantOrderHistory";
import { requestPermissionAndGetToken } from "../shared/messagesConsent";
import { ListRestaurantsPage } from "./restaurantManagement/listRestaurantsPage";
import MasterDataPage from "./masterData/MasterDataPage";

type adminDashboardStates = "Notifications" | "ListRestaurantsPage" | "masterData"

export function AdminPage() {
  const [restaurant, setRestaurants] = useState<FullProperty | null>(null);
  const [drawerOptions, setDrawerOptions] = useState<adminDashboardStates[]>([]);
  const [restaurantSaleItems, setRestaurantSaleItems] = useState<{ [restaurantId: string]: SaleItem[] }>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedDrawerItem, setSelectedDrawerItem] = useState<adminDashboardStates>("Notifications");
  const navigate = useNavigate();
  const { user, profile } = useSignedInUserState();
  const [openModal, setOpenModal] = useState(false);
  const [confirmationNumber, setConfirmationNumber] = useState("");
  const [orderDetails, setOrderDetails] = useState<{ orderId: string, orderSaleItems: string } | null>(null);
  const [isProcessingCollection, setIsProcessingCollection] = useState<boolean>(false);
  const { activeCount, loading: messagesLoading, messages, setMessages } = useActiveMessages(profile);
  const { pendingOrders, collectedOrders, loading: restaurantOrderLoading, error, refreshOrders } = useRestaurantOrderHistory(restaurant?.id ?? "");

  useEffect(() => {
    if (profile) {
      requestPermissionAndGetToken(profile);
    }
  }, [profile]);

  useEffect(() => {
    const fetchManagedRestaurants = async () => {
      try {
        setLoading(false);
        if (!user || !user.uid || !profile) {
          return;
        }
        setLoading(true);
        let data: FullProperty[] = [];
        if (profile.profileType === "admin") {
          setDrawerOptions([ "Notifications", "ListRestaurantsPage", "masterData" ] as adminDashboardStates[]);
          data = await restaurantService.fetchRestaurantsByOwner(user.uid);
        } else {
          navigate("/search");
        }
        console.log(data)
        setRestaurants(data[0]);

        const saleItemsPromises = data.map(async (restaurant) => {
          const saleItems = await restaurantService.fetchSaleItems(restaurant.id);
          return { [restaurant.id]: saleItems };
        });

        const resolvedSaleItems = await Promise.all(saleItemsPromises);
        setRestaurantSaleItems(resolvedSaleItems.reduce((acc, curr) => ({ ...acc, ...curr }), {}));
      } catch (err) {
        toast.error("Failed to fetch managed restaurants.");
      } finally {
        setLoading(false);
      }
    };

    fetchManagedRestaurants();
  }, [user?.uid, profile?.profileType]);

  useAuthRouting();


  const handleDrawerItemClick = (item: adminDashboardStates) => {
    setSelectedDrawerItem(item);
  };

  // Handle Process Collection modal submission
  const handleProcessCollection = async () => {
    setIsProcessingCollection(true);
    const collectOrder = await CartService.collectOrder({
      orderNumberId: parseInt(confirmationNumber),
      restaurantId: restaurant?.id ?? ""
    });
    if (collectOrder.success && collectOrder.order) {
      setConfirmationNumber("");
      var orderId = collectOrder.order.id;
      var orderDescription = ``;
      var promises = collectOrder.order.items.map(async (orderItem) => {
        orderDescription += `${orderItem.quantity}x ${orderItem.menuCartItem.name}\n`
      })
      await Promise.all(promises)
      setOrderDetails({ orderId: orderId, orderSaleItems: orderDescription });
    } else {
      alert(collectOrder.error);
    }
    setIsProcessingCollection(false)
    refreshOrders()
  };

  return (
    // <Box sx={{ display: "flex", minHeight: "100vh", }} className={'patlite'}>
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "row" }} className={'patlite'}>
      {/* Proccess Prder */}
      <Modal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setConfirmationNumber("");
          setOrderDetails(null);
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
          className="patlite"
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Process Order Collection
          </Typography>
          <TextField
            fullWidth
            label="Confirmation Number"
            value={confirmationNumber}
            onChange={(e) => !isProcessingCollection ? setConfirmationNumber(e.target.value) : null}
            margin="normal"
            disabled={!!orderDetails}
          />

          {isProcessingCollection ? (
            <CircularProgress size={24} sx={{ color: "black" }} />
          ) : (
            <Button
              variant="contained"
              onClick={handleProcessCollection}
              fullWidth
              sx={{
                mt: 1,
                background: "#162C1F",
                border: "1px solid #162C1F",
                borderRadius: "24px",
              }}
            >
              Confirm
            </Button>
          )}
          {orderDetails && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1">
                Order #{orderDetails.orderId}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Items: {orderDetails.orderSaleItems}
              </Typography>
            </Box>
          )}
        </Box>
      </Modal>
      {/* Left Sidebar */}
      <Drawer
        variant="permanent"
        anchor="left"
        sx={{
          width: 140,
          color: "#000",
          flexShrink: 0,
          minHeight: '100vh',
          [`& .MuiDrawer-paper`]: {
            width: 140,
            boxSizing: "border-box",
            backgroundColor: "#f5f9e9",
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      >
        <Box sx={{
          overflow: "auto", width: '100%', flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center',
          height: '100%',
          pb: 1,
          pt: 1
        }}>
          <List sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}>
            {drawerOptions.map((item, index) => {
              const isSelected = selectedDrawerItem === item;
              const iconStyles = isSelected ? { color: 'white', fontSize: 32, mb: 1 } : { fontSize: 32, mb: 1 };
              const textStyles = isSelected ? { color: 'white', fontSize: '14px' } : { fontSize: '14px' };

              return (
                <ListItem
                  key={index}
                  disablePadding
                  sx={{
                    width: '100%',
                    flexShrink: 1,
                    justifyContent: 'center',
                    backgroundColor: isSelected ? '#162C1F' : 'transparent',
                    borderRadius: 1,
                  }}
                >
                  <ListItemButton
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      padding: 1,
                    }}
                    onClick={() => handleDrawerItemClick(item)}
                  >
                    {item === "Notifications" &&
                      <Badge badgeContent={activeCount > 0 ? activeCount : null} color="error">
                        <MailIcon sx={iconStyles} />
                      </Badge>
                    }
                    {
                      item === "ListRestaurantsPage" &&
                        <StorefrontIcon sx={iconStyles} />
                    }
                    {
                      item === "masterData" &&
                        <EngineeringIcon sx={iconStyles} />
                    }
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={textStyles}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
          <AuthButton />
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {selectedDrawerItem === "Notifications" && <MessagesPage dashboardMessagesLoading={messagesLoading} dashboardMessages={messages} dashboardSetMessages={setMessages} />}
        {selectedDrawerItem === "ListRestaurantsPage" && <ListRestaurantsPage />}
        {selectedDrawerItem === "masterData" && <MasterDataPage />}
      </Box>
    </Box>
  );
}